import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Download, Image as ImageIcon, Instagram, Loader2, Package, Search } from "lucide-react";
import { toast } from "sonner";
import BackOffice from "@/components/account/BackOffice";
import { Btn, Chip, PageHead, Pill, Section, inputCls } from "@/components/account/Shell";
import api, { formatApiError, mediaUrl } from "@/lib/api";

const ELEMENTS = [
    ["photo", "Product photo"],
    ["name", "Product name"],
    ["price", "Price"],
    ["logo", "Shop logo"],
    ["instagram", "Instagram handle"],
    ["coupon", "Coupon code badge"],
    ["footer", "Order-at footer"],
    ["qr", "QR code"],
];

const KEY = "dropcards.options";

async function detail(e) {
    try {
        const text = await e.response?.data?.text?.();
        return formatApiError(JSON.parse(text).detail);
    } catch {
        return formatApiError(e.response?.data?.detail);
    }
}

function Box({ on, onChange, label, testid, note }) {
    return (
        <button type="button" onClick={() => onChange(!on)} data-testid={testid}
            className="flex items-center gap-2 border border-[#d0d0d0] bg-white px-2 py-1 text-left hover:border-[#1a1a1a]">
            <span className={`flex h-[15px] w-[15px] shrink-0 items-center justify-center border ${on
                ? "border-[#1a1a1a] bg-[#1a1a1a]" : "border-[#d0d0d0] bg-white"}`}>
                {on && <Check size={11} className="text-white" strokeWidth={3.5} />}
            </span>
            <span className="min-w-0 truncate text-[13px] font-semibold text-[#1a1a1a]">{label}</span>
            {note && <span className="ml-auto shrink-0 text-[11px] font-semibold uppercase text-[#4a4a4a]">{note}</span>}
        </button>
    );
}

function ProductRow({ p, on, onToggle }) {
    return (
        <button type="button" onClick={onToggle} data-testid={`drop-pick-${p.slug}`}
            className={`flex w-full items-center gap-2 border-t border-[#d0d0d0] px-2 py-1 text-left first:border-t-0 ${on ? "bg-[#eff4ff]" : "hover:bg-[#fafafa]"}`}>
            <span className={`flex h-[15px] w-[15px] shrink-0 items-center justify-center border ${on
                ? "border-[#1a56db] bg-[#1a56db]" : "border-[#d0d0d0] bg-white"}`}>
                {on && <Check size={11} className="text-white" strokeWidth={3.5} />}
            </span>
            <span className="h-8 w-9 shrink-0 overflow-hidden border border-[#d0d0d0] bg-[#fafafa]">
                {p.image && <img src={mediaUrl(p.image)} alt="" className="h-full w-full object-contain" />}
            </span>
            <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-[#1a1a1a]">{p.name}</span>
            <span className="hidden shrink-0 text-[12px] font-semibold tabular-nums text-[#1a1a1a] sm:inline">
                {p.price_label}
            </span>
            {!p.has_photo && <Chip tone="amber" testid={`drop-nophoto-${p.slug}`}>no photo</Chip>}
            {!p.active && <Chip tone="neutral" testid={`drop-hidden-${p.slug}`}>hidden</Chip>}
        </button>
    );
}

export default function DropCards() {
    const [params] = useSearchParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [picked, setPicked] = useState([]);
    const [cut, setCut] = useState("square");
    const [zipCuts, setZipCuts] = useState({ square: true, story: true, collection: false });
    const [couponId, setCouponId] = useState("");
    const [search, setSearch] = useState("");
    const [listedOnly, setListedOnly] = useState(true);
    const [preview, setPreview] = useState("");
    const [busy, setBusy] = useState(false);
    const [zipping, setZipping] = useState(false);
    const [opts, setOpts] = useState(() => {
        try { return JSON.parse(localStorage.getItem(KEY)) || null; } catch { return null; }
    });
    const blobRef = useRef("");

    useEffect(() => {
        api.get("/store/drop-cards").then(({ data: d }) => {
            setData(d);
            setOpts((o) => o || d.defaults);
            const slug = params.get("slug");
            const kind = params.get("kind") || "shirt";
            const first = d.products.find((p) => p.slug === slug)
                || d.products.find((p) => p.active && p.has_photo) || d.products[0];
            if (first) setPicked([{ kind: slug ? kind : first.kind, slug: first.slug }]);
            if (d.coupons?.length) setCouponId(d.coupons[0].id);
        }).catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Could not open the studio"));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { if (opts) localStorage.setItem(KEY, JSON.stringify(opts)); }, [opts]);

    const body = useMemo(() => ({
        items: picked, format: cut, options: opts || {}, coupon_id: couponId || null,
    }), [picked, cut, opts, couponId]);

    const render = useCallback(async () => {
        if (!opts || !picked.length) return;
        if (cut === "collection" && picked.length < 2) { setPreview(""); return; }
        setBusy(true);
        try {
            const res = await api.post("/store/drop-cards/render", body, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            if (blobRef.current) URL.revokeObjectURL(blobRef.current);
            blobRef.current = url;
            setPreview(url);
        } catch (e) {
            toast.error(await detail(e) || "Could not draw that card");
        } finally { setBusy(false); }
    }, [body, cut, opts, picked.length]);

    useEffect(() => {
        const t = setTimeout(render, 350);
        return () => clearTimeout(t);
    }, [render]);

    useEffect(() => {
        if (cut === "collection" && picked.length < 2) setPreview("");
    }, [cut, picked.length]);

    const toggle = (p) => setPicked((rows) => rows.some((r) => r.slug === p.slug)
        ? rows.filter((r) => r.slug !== p.slug)
        : [...rows, { kind: p.kind, slug: p.slug }]);

    const save = (name) => {
        if (!preview) return;
        const a = document.createElement("a");
        a.href = preview;
        a.download = name;
        a.click();
    };

    const downloadZip = async () => {
        const formats = Object.entries(zipCuts).filter(([, v]) => v).map(([k]) => k);
        if (!formats.length) { toast.error("Tick at least one cut"); return; }
        setZipping(true);
        try {
            const res = await api.post("/store/drop-cards/batch",
                { items: picked, formats, options: opts, coupon_id: couponId || null },
                { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${data.store.slug}-drop-cards.zip`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(`${res.headers["x-card-count"] || ""} cards zipped`.trim());
        } catch (e) {
            toast.error(await detail(e) || "Could not build the zip");
        } finally { setZipping(false); }
    };

    if (err) return (
        <BackOffice><div className="mx-auto max-w-2xl py-20 text-center" data-testid="drop-locked">
            <Instagram size={34} className="mx-auto text-[#4a4a4a]" />
            <h1 className="mt-4 text-xl font-bold text-[#1a1a1a]">Drop cards come with a space.</h1>
            <p className="mt-2 text-sm text-[#4a4a4a]">{err}</p>
            <Link to="/account/sell" className="mt-5 inline-block"><Btn tone="solid" testid="drop-open-space">See the spaces</Btn></Link>
        </div></BackOffice>
    );

    if (!data || !opts) return (
        <BackOffice><div className="flex min-h-[40vh] items-center justify-center">
            <Loader2 className="animate-spin text-[#4a4a4a]" /></div></BackOffice>
    );

    const rows = data.products.filter((p) => (!listedOnly || p.active)
        && (!search || p.name.toLowerCase().includes(search.toLowerCase())));
    const cutMeta = data.formats.find((f) => f.id === cut);
    const first = picked[0] ? data.products.find((p) => p.slug === picked[0].slug) : null;
    const allOn = ELEMENTS.every(([k]) => opts[k]);

    return (
        <BackOffice>
            <PageHead kicker="Marketing" title="Instagram drop cards" testid="drop-head"
                sub="Pick a listing, tick what goes on it, and download a ready-to-post 1080px card in your shop's own colors."
                right={<Chip tone="ink" testid="drop-picked-count">{picked.length} picked</Chip>} />

            <div className="mt-3 space-y-2.5">
                <div className="space-y-2.5">
                    <Section title="What's on the card" testid="drop-elements"
                        right={
                            <Btn testid="drop-toggle-all"
                                onClick={() => setOpts(Object.fromEntries(ELEMENTS.map(([k]) => [k, !allOn])))}>
                                {allOn ? "Uncheck all" : "Check all"}
                            </Btn>
                        }>
                        <div className="flex flex-wrap gap-1">
                            {ELEMENTS.map(([k, label]) => (
                                <Box key={k} on={!!opts[k]} label={label} testid={`drop-opt-${k}`}
                                    onChange={(v) => setOpts((o) => ({ ...o, [k]: v }))}
                                    note={k === "logo" && !data.store.logo ? "none yet"
                                        : k === "instagram" && !data.store.instagram ? "none yet" : null} />
                            ))}
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-[#d0d0d0] pt-2">
                            <span className="text-[11px] font-semibold uppercase text-[#4a4a4a]">Coupon on the badge</span>
                            <select value={couponId} onChange={(e) => setCouponId(e.target.value)}
                                disabled={!data.coupons.length} data-testid="drop-coupon"
                                className={`${inputCls} max-w-[19rem] flex-1`}>
                                {!data.coupons.length && <option value="">No live coupon yet</option>}
                                {data.coupons.map((c) => (
                                    <option key={c.id} value={c.id}>{`${c.value_label} · ${c.code}`}</option>
                                ))}
                            </select>
                            {!data.coupons.length && (
                                <Link to="/account/coupons" data-testid="drop-make-coupon"
                                    className="text-[12px] font-semibold text-[#1a56db] hover:underline">Write one →</Link>
                            )}
                            {(!data.store.logo || !data.store.instagram) && (
                                <Link to="/account/store" data-testid="drop-fix-brand"
                                    className="text-[12px] font-semibold text-[#1a56db] hover:underline">
                                    Add your {!data.store.logo ? "logo" : "@handle"} →
                                </Link>
                            )}
                        </div>
                    </Section>

                    <Section title="Your listings" pad={false} testid="drop-products"
                        note={`${rows.length} shown · ${data.listed} listed`}
                        right={
                            <div className="flex items-center gap-1.5">
                                <span className="relative">
                                    <Search size={12} className="absolute left-1.5 top-2 text-[#4a4a4a]" />
                                    <input value={search} onChange={(e) => setSearch(e.target.value)}
                                        placeholder="Find a product" data-testid="drop-search"
                                        className={`${inputCls} w-40 pl-5`} />
                                </span>
                                <Pill active={listedOnly} testid="drop-listed-only"
                                    onClick={() => setListedOnly((v) => !v)}>Listed only</Pill>
                                <Btn testid="drop-clear" onClick={() => setPicked([])}>Clear</Btn>
                            </div>
                        }>
                        <div className="max-h-[26rem] overflow-y-auto">
                            {rows.length ? rows.map((p) => (
                                <ProductRow key={`${p.kind}-${p.slug}`} p={p} onToggle={() => toggle(p)}
                                    on={picked.some((r) => r.slug === p.slug)} />
                            )) : (
                                <p className="p-3 text-sm text-[#4a4a4a]" data-testid="drop-empty">
                                    Nothing matches. <Link to="/account/products" className="font-semibold text-[#1a56db]">Go list something →</Link>
                                </p>
                            )}
                        </div>
                    </Section>
                </div>

                <div className="space-y-2.5">
                    <Section title="Preview" testid="drop-preview-panel"
                        note={cutMeta ? `${cutMeta.label} · ${cutMeta.size}` : ""}>
                        <div className="flex flex-wrap gap-1">
                            {data.formats.map((f) => (
                                <Pill key={f.id} active={cut === f.id} testid={`drop-cut-${f.id}`}
                                    onClick={() => setCut(f.id)}>{f.label}</Pill>
                            ))}
                        </div>

                        <div className="relative mt-2 flex min-h-[18rem] items-center justify-center border border-[#d0d0d0] bg-[#f5f5f5] p-2">
                            {preview ? (
                                <img src={preview} alt="Drop card preview" data-testid="drop-preview-img"
                                    className="max-h-[30rem] w-auto max-w-full" />
                            ) : (
                                <p className="px-4 text-center text-sm text-[#4a4a4a]" data-testid="drop-preview-empty">
                                    {cut === "collection" && picked.length < 2
                                        ? "A collection sheet needs 2 to 4 products — pick a couple more."
                                        : "Pick a listing to see its card."}
                                </p>
                            )}
                            {busy && (
                                <span className="absolute inset-0 flex items-center justify-center bg-white/70"
                                    data-testid="drop-rendering"><Loader2 className="animate-spin text-[#1a1a1a]" /></span>
                            )}
                        </div>

                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                            <Btn tone="solid" disabled={!preview} testid="drop-download-one"
                                onClick={() => save(`${data.store.slug}-${cut === "collection" ? "collection" : first?.slug || "card"}-${cut}.png`)}>
                                <Download size={12} /> Download PNG
                            </Btn>
                            {first && cut !== "collection" && (
                                <span className="text-[12px] font-semibold text-[#4a4a4a]" data-testid="drop-showing">
                                    showing {first.name}
                                </span>
                            )}
                        </div>
                    </Section>

                    <Section title="Download a batch" testid="drop-batch"
                        note={`${picked.length} product${picked.length === 1 ? "" : "s"} picked`}>
                        <div className="space-y-1">
                            {data.formats.map((f) => (
                                <Box key={f.id} on={!!zipCuts[f.id]} label={f.label} note={f.size}
                                    testid={`drop-zip-${f.id}`}
                                    onChange={(v) => setZipCuts((z) => ({ ...z, [f.id]: v }))} />
                            ))}
                        </div>
                        <Btn tone="solid" disabled={!picked.length || zipping} testid="drop-download-zip"
                            onClick={downloadZip}>
                            {zipping ? <Loader2 size={12} className="animate-spin" /> : <Package size={12} />}
                            Download ZIP
                        </Btn>
                        <p className="mt-1.5 text-[12px] text-[#4a4a4a]">
                            One PNG per product per cut. The collection sheet packs four products a page.
                        </p>
                    </Section>

                    <Section title="Posting notes" testid="drop-notes">
                        <ul className="space-y-1 text-[13px] text-[#4a4a4a]">
                            <li className="flex gap-1.5"><ImageIcon size={13} className="mt-0.5 shrink-0" />
                                Cards are drawn at full 1080px — no upscaling when Instagram compresses them.</li>
                            <li className="flex gap-1.5"><Instagram size={13} className="mt-0.5 shrink-0" />
                                The QR and footer point at {data.site}/stores/{data.store.slug}, so a screenshot still sells.</li>
                        </ul>
                    </Section>
                </div>
            </div>
        </BackOffice>
    );
}
